import React from 'react';
import { observable, action } from 'mobx';
import { useObserver, observer } from 'mobx-react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';

import keyMap from 'utils/keyMap';

import {
  TreeStyled,
  ClipListStyled,
  ClipPlaneStyled,
  ClipPlaneRow,
  ClipLabelStyled,
  ClipSliderStyled,
  ButtonsWrapper,
  ButtonStyled,
  SliderWrapper,
  SliderInput,
  CheckboxStyled,
  TextWrapper,
  InputStyled,
  SymbolWrapper,
} from './sectionBrowser.styled';

const ANG_MAX = 90.0;

const CLIP_PAR = {
  PITCH: 0,
  ROLL: 1,
  OFFSET: 2,
};

class ClipPlaneState {
  @observable PITCH_TXT = '0';
  @observable ROLL_TXT = '0';
  @observable OFFSET_TXT = '0';
  @observable isDirty = false;

  PITCH = 0;
  ROLL = 0;
  OFFSET = 0;

  constructor(pitch, roll, offset) {
    this.PITCH = pitch;
    this.ROLL = roll;
    this.OFFSET = offset;

    this.PITCH_TXT = pitch.toString();
    this.ROLL_TXT = roll.toString();
    this.OFFSET_TXT = offset.toString();
  }

  getPropText = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH_TXT;
      case CLIP_PAR.ROLL:
        return this.ROLL_TXT;
      case CLIP_PAR.OFFSET:
        return this.OFFSET_TXT;
      default:
        break;
    }
    return null;
  };

  getPropValue = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH;
      case CLIP_PAR.ROLL:
        return this.ROLL;
      case CLIP_PAR.OFFSET:
        return this.OFFSET;
      default:
        break;
    }
    return null;
  };

  @action setPropText = (propIx, txt) => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        this.PITCH_TXT = txt.toString();
        break;
      case CLIP_PAR.ROLL:
        this.ROLL_TXT = txt.toString();
        break;
      case CLIP_PAR.OFFSET:
        this.OFFSET_TXT = txt.toString();
        break;
      default:
        break;
    }
    this.isDirty = true;
  };

  @action setPropValue = (propIx, value) => {
    const isNumber = !Number.isNaN(value.toString());
    const valNum = isNumber ? Math.round(parseFloat(value.toString())) : null;

    switch (propIx) {
      case CLIP_PAR.PITCH:
        if (isNumber) {
          this.PITCH_TXT = value.toString();
          if (this.PICTH === valNum) return;
          this.PITCH = valNum;
        }
        break;
      case CLIP_PAR.ROLL:
        if (isNumber) {
          this.ROLL_TXT = value.toString();
          if (this.ROLL === valNum) return;
          this.ROLL = valNum;
        }
        break;
      case CLIP_PAR.OFFSET:
        if (isNumber) {
          this.OFFSET_TXT = value.toString();
          if (this.OFFSET === valNum) return;
          this.OFFSET = valNum;
        }
        break;
      default:
        break;
    }
    this.isDirty = false;
  };

  @action restoreProp = propIx => {
    this.setPropValue(propIx, this.getPropValue(propIx));
  };

  @action clear = () => {
    this.PITCH = 0;
    this.ROLL = 0;
    this.OFFSET = 0;

    this.PITCH_TXT = '0';
    this.ROLL_TXT = '0';
    this.OFFSET_TXT = '0';
  };

  isTemp = propIx => {
    switch (propIx) {
      case CLIP_PAR.PITCH:
        return this.PITCH.toString() !== this.PITCH_TXT;
      case CLIP_PAR.ROLL:
        return this.ROLL.toString() !== this.ROLL_TXT;
      case CLIP_PAR.OFFSET:
        return this.OFFSET.toString() !== this.OFFSET_TXT;
      default:
        break;
    }
    return false;
  };
}

export class SectionState {
  sceneRadius;
  @observable.ref sectionManager = null;
  @observable.ref engine = null;
  @observable clips = new Map();
  @observable updated = false;

  constructor(sectionManager, engine, sceneRadius) {
    this.engine = engine;
    this.sectionManager = sectionManager;
    this.sceneRadius = sceneRadius;
  }

  addClip = planeType => {
    const clipIx = this.sectionManager.GetSectionClipIx(planeType);

    if (!this.clips[clipIx]) {
      let pitch = 0;
      let roll = 0;
      let offset = 0;
      if (clipIx >= 1 && clipIx <= 3) {
        pitch = this.sectionManager.GetPitch(clipIx);
        roll = this.sectionManager.GetRoll(clipIx);
        offset = this.sectionManager.GetOffset(clipIx);
      }
      this.clips[clipIx] = new ClipPlaneState(pitch, roll, offset);
      this.updated = !this.updated;
    }

    return clipIx;
  };

  @action toggleClip = (clipIx, planeType) => {
    if (!this.sectionManager.IsActive()) this.sectionManager.SetActive(true);

    if (clipIx < 0) {
      // no active plane for this type, so switch ON
      for (let i = 1; i <= 3; i++) {
        const clip = this.sectionManager.GetClipSection(i);
        if (!clip.apply) {
          // free plane, use it
          this.sectionManager.SwitchOnClipSection(planeType);
          this.engine.UpdateClipPlane(i, clip._wasm);
          break;
        }
      }
    } else {
      // switch OFF
      this.sectionManager.SwitchOffClipSection(clipIx);
      for (let i = 1; i <= 3; i++) {
        const clip = this.sectionManager.GetClipSection(i);
        this.engine.UpdateClipPlane(i, clip._wasm);
      }
      this.clips[clipIx]?.clear();
    }

    this.updated = !this.updated;
  };

  isClipVisible = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip ? clip.visible : false;
    }
    return false;
  };

  isClipActive = clipIx => {
    if (clipIx >= 1 && clipIx <= 3) {
      const clip = this.sectionManager.GetClipSection(clipIx);
      return clip ? clip.apply : false;
    }
    return false;
  };

  @action toggleClipVisibility = clipIx => {
    if (!this.sectionManager.IsActive()) return;
    this.sectionManager.ToggleVisible(clipIx);
    this.updated = !this.updated;
  };

  @action reverseClip = clipIx => {
    if (!this.sectionManager.IsActive()) return;
    this.sectionManager.Reverse(clipIx);
    this.updated = !this.updated;
  };

  @action updateClip = (clipIx, propIx, magnitude, propVal) => {
    if (this.sectionManager.IsActive() === false) return;

    const val = parseFloat(magnitude);
    if (clipIx >= 1 && clipIx <= 3) {
      switch (propIx) {
        case CLIP_PAR.PITCH:
          this.sectionManager.RotatePitch(clipIx, val);
          break;
        case CLIP_PAR.ROLL:
          this.sectionManager.RotateRoll(clipIx, val);
          break;
        case CLIP_PAR.OFFSET:
          this.sectionManager.Offset(clipIx, val);
          break;
        default:
          break;
      }

      // update the shader uniforms
      const clip = this.sectionManager.GetClipSection(clipIx);
      this.engine.UpdateClipPlane(clipIx, clip._wasm);

      // update component state used to render slider values
      // - I added an additional argument propVal, so that I could set
      // the offset value (which is always between -100 to 100) to
      // something other then the offset magnitude (which is the
      // -100 to 100 range mapped to -radius to radius)
      if (propVal) {
        this.setPropValue(clipIx, propIx, propVal);
      } else {
        this.setPropValue(clipIx, propIx, val);
      }
    }
  };

  @action setPropText = (clipIx, propIx, txt) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].setPropText(propIx, txt);
    this.updated = !this.updated;
  };

  @action setPropValue = (clipIx, propIx, val) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].setPropValue(propIx, val);
    this.updated = !this.updated;
  };

  @action restoreProp = (clipIx, propIx) => {
    if (!this.clips[clipIx]) return;
    this.clips[clipIx].restoreProp(propIx);
    this.updated = !this.updated;
  };
}

const SectionBrowser = observer(() => {
  const viewerControl = useViewerControl();
  const { sectionState } = viewerControl.viewer;
  if (!sectionState) {
    return null;
  }

  const clipEnum = viewerControl?.viewer?.module.ClipPlaneType;
  return (
    <TreeStyled updated={sectionState.updated}>
      <ClipListStyled>
        <ClipPlane
          ix={1}
          sectionState={sectionState}
          clipType={clipEnum.ClipPlaneFront.value}
        />
        <ClipPlane
          ix={2}
          sectionState={sectionState}
          clipType={clipEnum.ClipPlaneRight.value}
        />
        <ClipPlane
          ix={3}
          sectionState={sectionState}
          clipType={clipEnum.ClipPlaneTop.value}
        />
      </ClipListStyled>
    </TreeStyled>
  );
});

export default SectionBrowser;

const ClipPlane = observer(props => {
  const { ix, sectionState, clipType } = props;

  // initial values
  const clipIx = sectionState.addClip(clipType);

  const isClipActive = () => {
    return sectionState.isClipActive(clipIx);
  };

  const isClipVisible = () => {
    return sectionState.isClipVisible(clipIx);
  };

  const toggleClipPlane = () => {
    sectionState.toggleClip(clipIx, clipType);
  };

  const toggleClipVisibility = () => {
    sectionState.toggleClipVisibility(clipIx);
  };

  const reverseClip = () => {
    sectionState.reverseClip(clipIx);
  };

  // render
  return (
    <ClipPlaneStyled
      id={ix}
      active={isClipActive()}
      isDirty={sectionState.clips[clipIx].isDirty}
    >
      <ClipPlaneRow>
        {/* TITLE */}
        <ClipLabelStyled active={isClipActive()} clipIx={ix}>
          {`Cut plane ${ix}`}
        </ClipLabelStyled>
        <ButtonsWrapper active={isClipActive()}>
          {/* reverse icon */}
          <ButtonStyled
            variant='tertiary'
            ariaLabel='Reverse Clip'
            icon='undo'
            onClick={reverseClip}
            appearance='plain'
          />
          {/* visibility icon */}
          <ButtonStyled
            variant='tertiary'
            ariaLabel='Clip Visibility'
            icon={isClipVisible() ? 'view' : 'view_off'}
            onClick={toggleClipVisibility}
            appearance='plain'
          />
        </ButtonsWrapper>
        {/* ON/OFF switch */}
        <CheckboxStyled
          id={ix}
          type='checkbox'
          checked={isClipActive()}
          onChange={toggleClipPlane}
        />
      </ClipPlaneRow>
      <>
        <ClipListStyled>
          {/* OFFSET */}
          <ClipSlider
            clipIx={clipIx}
            name='offset'
            symbol='%'
            sectionState={sectionState}
            propIx={CLIP_PAR.OFFSET}
            active={isClipActive()}
            min={-100}
            max={100}
            getClipType={props.getClipType}
            step={1}
          />
          {/* PICTH ANGLE */}
          <ClipSlider
            clipIx={clipIx}
            name='pitch'
            symbol='°'
            sectionState={sectionState}
            propIx={CLIP_PAR.PITCH}
            active={isClipActive()}
            min={-ANG_MAX}
            max={ANG_MAX}
            getClipType={props.getClipType}
          />
          {/* ROLL ANGLE */}
          <ClipSlider
            clipIx={clipIx}
            name='roll'
            symbol='°'
            sectionState={sectionState}
            propIx={CLIP_PAR.ROLL}
            active={isClipActive()}
            min={-ANG_MAX}
            max={ANG_MAX}
            getClipType={props.getClipType}
          />
        </ClipListStyled>
      </>
    </ClipPlaneStyled>
  );
});

function ClipSlider(props) {
  const { clipIx, name, symbol, propIx, active, max, min, sectionState, step } =
    props;
  const clipPlaneChange = magnitude => {
    if (name === 'offset') {
      // - handle offset differently here cause previous approach did not work,
      // radius < 1.0 was not handled well because slider steps are always 1
      // - so instead of trying to use the radius in min max for offset, I just
      // set the range to -100 to 100 always, then map the slider value to the
      // -radius to radius range here and pass that as magnitude
      // - since sectionState.updateClip also sets the slider value (to update
      // the slider visually), I also need to pass the original magnitude as
      // unmapped argument so updateClip can use that when updating the offset
      // slider
      const radius = sectionState.sceneRadius || 100.0;
      sectionState.updateClip(
        clipIx,
        propIx,
        magnitude * (radius / 100.0),
        magnitude,
      );
    } else {
      sectionState.updateClip(clipIx, propIx, magnitude);
    }
  };

  const handleTextInput = e => {
    const isNumber = e.target.value.length > 0 && !Number.isNaN(e.target.value);
    const val = parseFloat(e.target.value);
    let txtValue = e.target.value;
    if (isNumber) {
      txtValue = val.toString();
      // check limits
      if (val < min) return;
      if (val > max) return;
    }
    // update the text...
    sectionState.setPropText(clipIx, propIx, txtValue);
  };

  const onKeyDown = e => {
    const valTxt = sectionState.clips[clipIx].getPropText(propIx);
    const isNumber = valTxt.length > 0 && !Number.isNaN(valTxt);

    switch (e.key.toUpperCase()) {
      case keyMap.ESCAPE:
        sectionState.restoreProp(clipIx, propIx);
        e.stopPropagation();
        break;
      case keyMap.ENTER:
        if (isNumber) {
          clipPlaneChange(parseFloat(valTxt));
        }
        e.stopPropagation();
        break;
      case keyMap.BACKSPACE:
        // deleting...
        break;
      default:
        break;
    }
  };

  const clipState = sectionState.clips[clipIx];
  if (!clipState) return null;
  const valNum = clipState.getPropValue(propIx);
  const valTxt = clipState.getPropText(propIx);
  const isTemp = clipState.isTemp(propIx);

  return useObserver(() => (
    <ClipSliderStyled active={active}>
      <div>
        <small>{name}</small>
      </div>
      <TextWrapper isTemp={isTemp}>
        <InputStyled
          name={name}
          value={valTxt}
          isTemp={isTemp}
          onChange={e => {
            handleTextInput(e);
          }}
          onKeyDown={e => {
            onKeyDown(e);
          }}
          type='text'
          size='small'
          isGhost
        />
      </TextWrapper>
      <SymbolWrapper>{symbol}</SymbolWrapper>
      <SliderWrapper>
        <SliderInput
          active={active}
          onChange={e => {
            clipPlaneChange(e.target.value);
          }}
          type='range'
          value={valNum}
          min={min}
          max={max}
          step={step}
        />
      </SliderWrapper>
    </ClipSliderStyled>
  ));
}
